<script setup>
/**
 * TalkCard: a pulled card, as one cream panel in the dark chat (2026-10-21
 * talk card design; 2026-10-22 gift redesign §8). One slip of paper on the
 * table, used for both Talk it through's pulled card (label "Pulled") and the
 * gift room's drawn card (label "Your card").
 *
 * Top to bottom: "YOUR QUESTION" and the question; a hairline; the card art
 * (a portrait crop, turned 180° when reversed), the eyebrow
 * "<LABEL> · <Card>, reversed", the 3 to 5 word phrase as a headline, then the
 * Mystic's reading. Until the reading arrives: soft placeholder lines and
 * "<Mystic> is reading it for you…". An old card with no phrase has no
 * headline (no gap, no placeholder). At the pick the card turns face up once
 * (about 0.9s; nothing turns with reduced motion). The art opens card detail.
 */
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { writeInMs, prefersReducedMotion } from '@/utils/lesson-presence'

const props = defineProps({
  label: { type: String, default: 'Pulled' },
  // The question's eyebrow: "The question you held" for a gift card (DESIGN-2 §37).
  questionLabel: { type: String, default: 'Your question' },
  question: { type: String, default: '' },
  card: { type: String, default: '' },
  image: { type: String, default: '' },
  reversed: { type: Boolean, default: false },
  line: { type: String, default: '' },
  reading: { type: String, default: '' },
  readerName: { type: String, default: '' },
  // The words are on their way (the pick has landed, the read has not).
  waiting: { type: Boolean, default: false },
  // Turn the card face up on mount (a live pick; never on a replay).
  turn: { type: Boolean, default: false },
  cardId: { type: [String, Number], default: '' },
  // A lesson's card (2026-10-28): centred at the panel width, its chips under it.
  centered: { type: Boolean, default: false },
  // Still settling into the panel (the picked card is on its way from the
  // fan): the face is hidden and it has not turned; it turns once released.
  held: { type: Boolean, default: false },
  // The reading writes in when it arrives on a card drawn just now (never on
  // a replay), after the turn; `written` says it is done (the chips follow).
  writeIn: { type: Boolean, default: false },
})
const emit = defineEmits(['card-detail', 'written'])

const quoted = computed(() => {
  const q = String(props.question || '').trim().replace(/^["“]+|["”]+$/g, '')
  return q ? `“${q}”` : ''
})
const reader = computed(() => (props.readerName || '').trim() || 'The Mystic')
const showWaiting = computed(() => props.waiting && !props.reading)
const artFailed = ref(false)

// The write-in: once the reading is here and the card has settled.
const writing = ref(false)
const writeMs = ref(0)
let writeTimer = null
let written = false
function writeNow () {
  if (written || !props.reading || props.held) return
  written = true
  if (!props.writeIn) { emit('written'); return }
  const ms = writeInMs(props.reading, { reduced: prefersReducedMotion() })
  if (!ms) { emit('written'); return }
  writeMs.value = ms
  writing.value = true
  // The headline leads by a beat, then the words sweep down.
  writeTimer = setTimeout(() => { writing.value = false; emit('written') }, ms + 350)
}
watch(() => [props.reading, props.held], writeNow, { immediate: true })
onBeforeUnmount(() => { if (writeTimer) clearTimeout(writeTimer) })
</script>

<template>
  <article class="talk-card" :class="{ 'is-waiting': showWaiting, 'is-turning': turn && !held, 'is-held': held, 'is-centered': centered, 'is-written-in': writeIn }"
           data-testid="talk-card" :data-card-id="cardId || null" :data-lesson-card="centered ? '' : null">
    <header v-if="quoted" class="tc-q">
      <p class="tc-eyebrow">{{ questionLabel }}</p>
      <p class="tc-question" data-testid="talk-card-question">{{ quoted }}</p>
    </header>

    <div class="tc-body">
      <button type="button" class="tc-art" :aria-label="`${card}${reversed ? ', reversed' : ''}: what it means`"
              data-testid="talk-card-art" @click="emit('card-detail')">
        <span class="tc-flip" :class="{ rev: reversed }">
          <span class="tc-face">
            <img v-if="image && !artFailed" :src="image" :alt="card" @error="artFailed = true" />
            <span v-else class="tc-blank" aria-hidden="true">✦</span>
          </span>
        </span>
      </button>
      <p class="tc-eyebrow tc-card" data-testid="talk-card-eyebrow">
        {{ label }}<span class="tc-dot" aria-hidden="true"> · </span><span class="tc-name">{{ card }}<template v-if="reversed">, reversed</template></span>
      </p>
      <template v-if="showWaiting">
        <span class="tc-ph tc-ph-head" aria-hidden="true"></span>
        <span class="tc-ph" aria-hidden="true"></span>
        <span class="tc-ph tc-ph-2" aria-hidden="true"></span>
        <span class="tc-ph tc-ph-3" aria-hidden="true"></span>
        <p class="tc-waiting" role="status" data-testid="talk-card-waiting">{{ reader }} is reading it for you…</p>
      </template>
      <template v-else>
        <h3 v-if="line" class="tc-line" data-testid="talk-card-line">{{ line }}</h3>
        <p v-if="reading" class="tc-reading" :class="{ 'is-writing': writing }" :style="writing ? { '--tc-write-ms': writeMs + 'ms' } : null"
           data-testid="talk-card-reading">{{ reading }}</p>
      </template>
    </div>
  </article>
</template>

<style scoped>
.talk-card {
  --paper: #fdfaf2; --edge: #e4d6b8; --gold: #c9a94e; --gold-dk: #8a6a2a; --ink: #2a1845; --ink3: #3d2f55; --hush: #7a6a8f;
  --display: 'Letter Display', 'Cinzel', Georgia, serif;
  --serif: 'Letter Serif', 'Cormorant Garamond', Georgia, serif;
  --art-w: 78px; --art-h: 126px;
  position: relative; box-sizing: border-box; width: 100%; max-width: 760px; margin: 14px 0; align-self: stretch; flex: none;
  padding: 18px 16px 20px; overflow: hidden;
  background: var(--paper); color: var(--ink); text-align: left;
  border: 1px solid var(--edge); border-radius: 22px;
  box-shadow: 0 22px 40px -26px rgba(0, 0, 0, 0.85);
  animation: tc-in 320ms ease-out both;
}
/* A gold rule across the top edge, brighter in the middle. */
.talk-card::before {
  content: ''; position: absolute; left: 14%; right: 14%; top: 0; height: 3px; border-radius: 0 0 3px 3px;
  background: linear-gradient(90deg, rgba(201, 169, 78, 0), var(--gold) 30%, #e7c983 50%, var(--gold) 70%, rgba(201, 169, 78, 0));
}
@keyframes tc-in { from { opacity: 0; transform: translateY(8px); } }

.tc-eyebrow { margin: 0; font: 600 11px/1.4 var(--display); letter-spacing: 0.2em; text-transform: uppercase; color: var(--gold-dk); }
.tc-q { padding-bottom: 14px; margin-bottom: 16px; border-bottom: 1px solid var(--edge); }
.tc-question { margin: 6px 0 0; font: italic 500 18px/1.45 var(--serif); color: var(--ink3); text-wrap: pretty; overflow-wrap: anywhere; }

.tc-body { display: flow-root; }
.tc-art {
  float: left; margin: 2px 16px 8px 0; padding: 0; border: 0; background: none; cursor: zoom-in;
  width: var(--art-w); height: var(--art-h); perspective: 700px;
}
.tc-art:focus-visible { outline: 2px solid #7b4bb0; outline-offset: 3px; border-radius: 8px; }
.tc-flip { position: relative; display: block; width: 100%; height: 100%; }
.tc-flip.rev .tc-face img { transform: rotate(180deg); }
.tc-face {
  position: absolute; inset: 0; display: block; overflow: hidden; border-radius: 8px;
  border: 1px solid var(--gold); box-shadow: 0 14px 22px -14px rgba(42, 24, 69, 0.8);
}
.tc-face img { display: block; width: 100%; height: 100%; object-fit: cover; }
.tc-blank {
  display: grid; place-items: center; width: 100%; height: 100%; color: #f3d98f; font-size: 22px;
  background: linear-gradient(160deg, #3a2566, #1c1030 60%, #2a1845);
}
/* The pick: the card turns face up, once. */
.talk-card.is-turning .tc-flip { animation: tc-turn 900ms cubic-bezier(.2, .7, .2, 1) both; }
/* No back face (WebKit flattens 3D inside the panel's rounded clip and showed
   the back): the card turns in from its edge, face up. */
@keyframes tc-turn {
  0% { transform: rotateY(90deg) translateY(6px); filter: brightness(0.6); }
  100% { transform: rotateY(0) translateY(0); filter: none; }
}

.tc-card { margin-bottom: 6px; }
.tc-dot { letter-spacing: 0; color: var(--hush); }
.tc-name { font: italic 500 15px/1.3 var(--serif); letter-spacing: 0.01em; text-transform: none; color: var(--hush); }
.tc-line { margin: 0 0 8px; font: 700 23px/1.15 var(--serif); color: var(--ink); text-wrap: balance; overflow-wrap: anywhere; }
.tc-reading { margin: 0; font: 500 17.5px/1.55 var(--serif); color: var(--ink3); white-space: pre-line; text-wrap: pretty; overflow-wrap: break-word; }

/* Waiting: soft lines where the words will be. */
.tc-ph {
  display: block; overflow: hidden; height: 12px; margin: 10px 0 0; border-radius: 6px;
  background: linear-gradient(90deg, #efe4cc, #f7f0e0 50%, #efe4cc); background-size: 200% 100%; animation: tc-shimmer 1.6s linear infinite;
}
.tc-ph-head { height: 22px; margin: 6px 22% 0 0; }
.tc-ph-2 { margin-right: 8%; }
.tc-ph-3 { margin-right: 40%; }
@keyframes tc-shimmer { to { background-position: -200% 0; } }
.tc-waiting { margin: 14px 0 0; font: italic 500 16px/1.4 var(--serif); color: var(--hush); }

@media (min-width: 700px) {
  .tc-art { --art-w: 112px; --art-h: 180px; width: 112px; height: 180px; margin-right: 22px; }
  .tc-line { font-size: 28px; }
  .tc-question { font-size: 19px; }
  .tc-reading { font-size: 18px; }
}
@media (min-width: 700px) {
  .talk-card { padding: 22px 24px 24px; }
}
/* A lesson's card: centred at the panel width (its chips sit under it at the
   same width); on a phone, full width less the gutter the chat sets. */
.talk-card.is-centered {
  width: calc(100% - var(--tc-gutter-x, 0px)); max-width: 680px; margin: 14px auto; align-self: center;
}
/* Settling: the picked card is on its way from the fan; the slot waits and
   the card's name comes with the turn. */
.talk-card.is-held .tc-flip { visibility: hidden; }
.talk-card.is-held .tc-name { opacity: 0; }
.talk-card.is-held.is-written-in .tc-line, .talk-card.is-held.is-written-in .tc-reading { visibility: hidden; }
.talk-card .tc-name { transition: opacity 500ms ease 300ms; }
/* The reading writes in: the headline first, then the words sweep down. */
.talk-card.is-written-in .tc-line { animation: tc-fade-in 520ms ease-out both; }
.tc-reading.is-writing {
  -webkit-mask-image: linear-gradient(to bottom, #000 40%, transparent 60%);
  mask-image: linear-gradient(to bottom, #000 40%, transparent 60%);
  -webkit-mask-size: 100% 250%; mask-size: 100% 250%;
  -webkit-mask-repeat: no-repeat; mask-repeat: no-repeat;
  animation: tc-write var(--tc-write-ms, 1600ms) cubic-bezier(.35, .1, .3, 1) 350ms both;
}
@keyframes tc-write {
  from { -webkit-mask-position: 0 100%; mask-position: 0 100%; }
  to { -webkit-mask-position: 0 0; mask-position: 0 0; }
}
@keyframes tc-fade-in { from { opacity: 0; transform: translateY(4px); } }
@media (prefers-reduced-motion: reduce) {
  .talk-card, .talk-card.is-turning .tc-flip, .tc-ph, .tc-reading.is-writing, .talk-card.is-written-in .tc-line { animation: none; }
  .talk-card .tc-name { transition: none; }
}
</style>
